import { useFn } from '@eturi/react'
import { useT } from '@eturi/react-i18next'
import { assertNotNullish } from '@eturi/util'
import type { SSOLoginScheme } from '@op/services'
import { checkAccountSSO, ssoLogin } from '@op/services'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { landingView$, setSSO, setView as setLandingView } from '../../reducers/landing.slice'
import { useAppDispatch } from '../../types'
import type { LandingView } from '../../types/LandingView'
import { useToasts } from '../../widgets'
import { getSSOClientParams } from './sso-clients'

/**
 * Returns a function that handles an SSO auth code. Performs login or redirects if necessary,
 * and handles any errors with toasts.
 */
export const useLandingSSOHandler = () => {
	const d = useAppDispatch()
	const [t] = useT()
	const history = useHistory()
	const landingView = useSelector(landingView$)
	const { pathname } = useLocation()
	const { addErrorToast } = useToasts()

	const redirectIfNeeded = (to: LandingView | '/') => {
		// From '/' to '/'
		if (pathname == to) return
		if (pathname == '/') {
			// To the same landing view
			if (landingView == to) return
			// Set landing view if we're at room and need to change views
			if (to != '/') return d(setLandingView(to))
		}

		// Redirect
		history.replace(to == '/' ? to : `/?view=${to}`)
	}

	return useFn(async (auth_code: string, type: SSOLoginScheme, userName?: string) => {
		try {
			const { client_id, redirect_uri } = getSSOClientParams(type)
			const accountSSOStatus = await d(
				checkAccountSSO({ auth_code, client_id, redirect_uri, type }),
			)

			assertNotNullish(accountSSOStatus)

			const { account_exists, can_login_with_token, id_token, token_valid } = accountSSOStatus

			if (account_exists) {
				// If the SSO account exists and, we can log in with the token, just do it
				if (can_login_with_token) {
					return await d(ssoLogin({ id_token, type })).unwrap()
				}

				/**
				 * If the account exists, and we can't use the token for logging in, redirect to the
				 * password login page. Use query param to ensure any saved view is overwritten.
				 *
				 * NOTE: This info is fine, since they've gone through authentication. Meaning, it's not
				 *  a form that can just be hit by anyone.
				 */
				addErrorToast(t('error_strings.wrong_login'))
				return redirectIfNeeded('login')
			}

			/**
			 * The account doesn't exist, but the token is valid, so set the SSO state, and redirect to
			 * the Sign-Up page.
			 */
			if (token_valid) {
				d(setSSO({ id_token, type, userName }))
				return redirectIfNeeded('signup')
			}
		} catch {
			//
		}

		addErrorToast(t('error_strings.auth_failed'))
		redirectIfNeeded('/')
	})
}
